import { v4 as uuidv4 } from 'uuid';
import { action, computed, observable, reaction, runInAction } from 'mobx';
import { DataCrafterTemplatesVisualStore } from '.';
import { message } from 'antd';
import axios, { CancelTokenSource, CancelToken } from 'axios';
import {
    DataCrafterTemplate,
    DataCrafterTemplateColumn,
    DataCrafterInputColumn,
    CreateDataCrafterTemplateRequest,
    UpdateDataCrafterTemplateRequest,
    TemplateSubmitOptions
} from '../types';
import { Package } from '../../common/models';
import { DataCrafterTemplatesService } from '../services';
import { MLService } from '../../ml_models/services';
import { MLModel } from '../../ml_models/types';
import { groupBy } from 'lodash';
import CodeValidationContext from '../types/CodeValidationContext';
import DataCrafterScriptCompilationStore from './DataCrafterScriptCompilationStore';
import DataCrafterOutputVariantsStore from './DataCrafterOutputVariantsStore';

type SubmitOptions = TemplateSubmitOptions;

type PackageColumn = {
    key: string;
    name: string;
    worksheetName: string;
    headerCoordinates: string;
    packageId: string;
};

interface CompileProgress {
    step: number;
    total_steps: number;
    message: string;
    status: string;
    duration: number;
    step_details?: {
        model_output?: string;
        observations?: string;
        action_output?: string | null;
        tool_calls?: Array<unknown>;
    };
}

export default class DataCrafterTemplateFormStore {
    @observable
    selectedInputPackages: string[] = [];

    @observable
    selectedOutputPackage: string | null = null;

    @observable
    inputColumns: PackageColumn[] = [];

    @observable
    outputColumns: DataCrafterTemplateColumn[] = [];

    @observable
    isLoadingInputColumns: boolean = false;

    @observable
    isLoadingOutputColumns: boolean = false;

    @observable
    selectedTemplate: DataCrafterTemplate | null = null;

    @observable
    isLoadingTemplate: boolean = false;

    @observable
    mlModels: MLModel[] = [];

    @observable
    isLoadingMlModels: boolean = false;

    @observable
    templateName: string = '';

    @observable
    selectedCoderModelId: string | undefined = undefined;

    @observable
    selectedInputPackageId: string | undefined = undefined;

    @observable
    formChanged: boolean = false;

    // Temp id of the template that is being created (used to store the compilation state)
    @observable
    tempTemplateId: string = this.generateTempId();

    @observable
    codeValidationContext: CodeValidationContext | undefined = undefined;

    @observable
    outputColumnsActiveTab: string | undefined = undefined;

    private cancelTokenSource: CancelTokenSource | null = null;

    private readonly tempTemplateIdPrefix = 'temp-template-';

    constructor(
        private visualStore: DataCrafterTemplatesVisualStore,
        private dataCrafterTemplatesService: DataCrafterTemplatesService,
        private mlService: MLService,
        private scriptCompilationStore: DataCrafterScriptCompilationStore,
        private outputVariantsStore: DataCrafterOutputVariantsStore
    ) {
        reaction(
            () => this.inputColumnGroups,
            inputColumnGroups => {
                this.selectedInputPackageId = Object.keys(inputColumnGroups)[0];
            }
        );
    }

    @computed
    get currentTemplateId(): string {
        return this.selectedTemplate ? this.selectedTemplate.id : this.tempTemplateId;
    }

    @computed
    get currentTemplateScript(): string | null {
        return this.scriptCompilationStore.getDisplayTemplateScript(
            this.currentTemplateId,
            this.outputVariantsStore.selectedOutputVariant || undefined
        );
    }

    @computed
    get currentTemplateScriptError(): string | undefined {
        return this.scriptCompilationStore.getTemplateCompilationState(this.currentTemplateId).templateScriptError;
    }

    @computed
    get currentCompilationProgress(): CompileProgress | null {
        return this.scriptCompilationStore.getTemplateCompilationState(this.currentTemplateId).compilationProgress;
    }

    @computed
    get isCurrentTemplateLoading(): boolean {
        return this.scriptCompilationStore.getTemplateCompilationState(this.currentTemplateId).isLoadingTemplateScript;
    }

    @computed
    get canSubmit(): boolean {
        return (
            this.selectedOutputPackage !== null &&
            this.outputColumns.length > 0 &&
            this.outputColumns.some(col => col.mappingRule) &&
            !!this.templateName &&
            !!this.selectedCoderModelId
        );
    }

    @computed
    get canCompileScript(): boolean {
        return (
            this.selectedInputPackages.length > 0 &&
            this.selectedOutputPackage !== null &&
            this.outputColumns.length > 0 &&
            this.outputColumns.some(col => col.mappingRule) &&
            !!this.selectedCoderModelId
        );
    }

    @computed
    get hasScript(): boolean {
        return this.outputVariantsStore.selectedOutputVariant
            ? !!this.outputVariantsStore.selectedOutputVariant.scriptRefId ||
                  !!this.outputVariantsStore.selectedOutputVariant.code
            : !!this.displayTemplateScriptRefId || !!this.currentTemplateScript;
    }

    @computed
    get canPreviewScript(): boolean {
        return this.canCompileScript && this.hasScript;
    }

    @computed
    get canValidateScript(): boolean {
        return this.canCompileScript && !!this.displayTemplateScript;
    }

    @computed
    get isBusyWithScriptOperation(): boolean {
        return this.scriptCompilationStore.isBusyWithScriptOperation;
    }

    @computed
    get packages(): Package[] {
        return this.visualStore.packages;
    }

    @computed
    get isLoadingPackages(): boolean {
        return this.visualStore.loadingPackages;
    }

    @computed
    get isEditMode(): boolean {
        return this.selectedTemplate !== null;
    }

    @computed
    get hasMlModels(): boolean {
        return this.mlModels.length > 0;
    }

    @computed
    get inputColumnGroups() {
        return groupBy(this.inputColumns, 'packageId');
    }

    // ========== Output Variants Computed Properties ==========

    @computed
    get hasOutputVariants(): boolean {
        return this.outputVariantsStore.outputVariants.length > 0;
    }

    @computed
    get canCreateOutputVariant(): boolean {
        return this.outputVariantsStore.canCreateOutputVariantWithContext(this.selectedTemplate, this.outputColumns);
    }

    @computed
    get canManageOutputVariants(): boolean {
        return this.outputVariantsStore.canManageOutputVariantsWithContext(this.selectedTemplate);
    }

    @computed
    get displayOutputColumns(): DataCrafterTemplateColumn[] {
        return this.outputVariantsStore.selectedOutputVariant?.columns ?? this.outputColumns;
    }

    @computed
    get displayTemplateScript(): string | null {
        return this.outputVariantsStore.selectedOutputVariant?.code ?? this.currentTemplateScript;
    }

    @computed
    get displayTemplateScriptRefId(): string | null {
        return this.scriptCompilationStore.getTemplateScriptRefId(
            this.currentTemplateId,
            this.outputVariantsStore.selectedOutputVariant || undefined
        );
    }

    // Backward compatibility for picker script compilation state
    @computed
    get currentTemplateVariantPickerCompilationState() {
        return this.scriptCompilationStore.getTemplateVariantPickerCompilationState(this.currentTemplateId);
    }

    static getColumnLabelAndValue(column: PackageColumn) {
        const label = `${column.worksheetName}:${column.name}:${column.headerCoordinates}`.replace(/\n/g, ' ');
        return { label, value: `@'${label}'` };
    }

    @action.bound
    setTemplateName(name: string) {
        this.templateName = name;
    }

    @action.bound
    setSelectedCoderModelId(modelId: string | undefined) {
        this.selectedCoderModelId = modelId;
    }

    @action.bound
    setSelectedInputPackageId(selectedInputPackageId?: string) {
        this.selectedInputPackageId = selectedInputPackageId;
    }

    @action.bound
    generateTempTemplateId() {
        this.tempTemplateId = this.generateTempId();
        this.scriptCompilationStore.setCurrentTemplateId(this.tempTemplateId);
    }

    @action.bound
    setCodeValidationContext(context: CodeValidationContext | undefined) {
        this.codeValidationContext = context;
    }

    @action.bound
    setOutputColumnsActiveTab(tab: string | undefined) {
        this.outputColumnsActiveTab = tab;
    }

    @action.bound
    async initializeWithTemplate(templateId: string) {
        if (!this.visualStore.currentProject) {
            return;
        }

        try {
            runInAction(() => {
                this.isLoadingTemplate = true;
            });

            const template = (await this.dataCrafterTemplatesService.getTemplateById(
                templateId,
                this.visualStore.currentProject.id
            )) as DataCrafterTemplate | null;

            if (template && this.isValidTemplate(template)) {
                runInAction(() => {
                    this.selectedTemplate = template;
                    this.selectedOutputPackage = template.outputPackageId;
                    this.selectedInputPackages = template.inputPackageIds ?? [];
                    this.outputColumns = template.columns.map(
                        (col: DataCrafterTemplateColumn) =>
                            new DataCrafterTemplateColumn(
                                col.name,
                                col.worksheetName,
                                col.headerCoordinates,
                                col.packageId,
                                col.mappingRule
                            )
                    );
                    this.inputColumns =
                        template.inputColumns?.map((col: DataCrafterInputColumn, index: number) => ({
                            key: `${col.worksheetName}-${col.name}-${index}`,
                            name: col.name,
                            worksheetName: col.worksheetName,
                            headerCoordinates: col.headerCoordinates,
                            packageId: col.packageId
                        })) ?? [];

                    this.scriptCompilationStore.initializeWithTemplate(
                        template.id,
                        template.code,
                        template.variantPickerScript || undefined
                    );

                    this.templateName = template.name;
                    this.selectedCoderModelId = template.coderModelId;
                });

                // Load output variants using the dedicated store
                await this.outputVariantsStore.loadOutputVariants(template);

                await this.fetchOutputColumns();

                // Load ML models for the current project
                await this.loadMlModels();
            }
        } catch (error) {
            console.error('Failed to load template:', error);
            message.error('Failed to load template');
        } finally {
            runInAction(() => {
                this.isLoadingTemplate = false;
            });
        }
    }

    @action.bound
    async initialize() {
        if (this.visualStore.currentProject) {
            await this.loadMlModels();
        }

        this.generateTempTemplateId();
    }

    @action.bound
    setSelectedInputPackages(packageIds: string[]) {
        this.selectedInputPackages = packageIds;
        this.inputColumns = [];
    }

    @action.bound
    setSelectedOutputPackage(packageId: string | null) {
        this.selectedOutputPackage = packageId;
        this.outputColumns = [];
    }

    @action.bound
    async loadPackages() {
        await this.visualStore.loadAllProjectPackages();
    }

    @action.bound
    async loadMlModels() {
        if (!this.visualStore.currentProject) {
            return;
        }

        try {
            runInAction(() => {
                this.isLoadingMlModels = true;
            });

            const models = await this.mlService.getMLModelsForProject(this.visualStore.currentProject.id);

            runInAction(() => {
                this.mlModels = models;
            });
        } catch (error) {
            console.error('Failed to load ML models:', error);
            message.error('Failed to load ML models');
        } finally {
            runInAction(() => {
                this.isLoadingMlModels = false;
            });
        }
    }

    @action.bound
    async fetchInputColumns() {
        if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel();
        }

        if (!this.selectedInputPackages.length) {
            return runInAction(() => {
                this.isLoadingInputColumns = false;
                this.inputColumns = [];
            });
        }

        const currentCancelTokenSource = axios.CancelToken.source();
        this.cancelTokenSource = currentCancelTokenSource;

        try {
            runInAction(() => {
                this.isLoadingInputColumns = true;
            });

            const response = await this.fetchPackagesColumns(this.selectedInputPackages, this.cancelTokenSource.token);

            runInAction(() => {
                this.inputColumns = response.map((col, index) => ({
                    key: `${col.worksheetName}-${col.name}-${index}`,
                    name: col.name,
                    worksheetName: col.worksheetName,
                    headerCoordinates: col.headerCoordinates,
                    packageId: col.packageId
                }));
            });
        } catch (error) {
            console.error('Failed to fetch input columns:', error);
            message.error('Failed to fetch input package columns');
        } finally {
            // Only set isLoadingInputColumns to false if this is the latest request
            if (this.cancelTokenSource === currentCancelTokenSource) {
                runInAction(() => {
                    this.isLoadingInputColumns = false;
                });
            }
        }
    }

    @action.bound
    async fetchOutputColumns() {
        if (!this.selectedOutputPackage) {
            return;
        }

        try {
            runInAction(() => {
                this.isLoadingOutputColumns = true;
            });

            const response = await this.fetchPackagesColumns([this.selectedOutputPackage]);
            const existingMappings = new Map(
                this.outputColumns.map(col => [`${col.worksheetName}-${col.name}`, col.mappingRule])
            );

            if (response.length === 0) {
                return;
            }

            runInAction(() => {
                this.outputColumns = response.map(col => {
                    const key = `${col.worksheetName}-${col.name}`;
                    const existingMapping = existingMappings.get(key) || '';
                    return new DataCrafterTemplateColumn(
                        col.name,
                        col.worksheetName,
                        col.headerCoordinates,
                        col.packageId,
                        existingMapping
                    );
                });
            });
        } catch (error) {
            console.error('Failed to fetch output columns:', error);
            message.error('Failed to fetch output package columns');
        } finally {
            runInAction(() => {
                this.isLoadingOutputColumns = false;
            });
        }
    }

    @action.bound
    navigateToList() {
        this.visualStore.navigateToList();
    }

    @action.bound
    async createTemplate(
        templateName: string,
        coderModelId?: string,
        options: SubmitOptions = {}
    ): Promise<DataCrafterTemplate | null> {
        if (!this.canSubmit || !this.visualStore.currentProject) {
            return null;
        }

        try {
            this.templateName = templateName;
            this.selectedCoderModelId = coderModelId;

            const request: CreateDataCrafterTemplateRequest = {
                name: this.templateName,
                outputPackageId: this.selectedOutputPackage!,
                columns: this.outputColumns.filter(col => col.mappingRule),
                inputPackageIds: this.selectedInputPackages,
                inputColumns: this.inputColumns.map(col => ({
                    name: col.name,
                    worksheetName: col.worksheetName,
                    headerCoordinates: col.headerCoordinates,
                    packageId: col.packageId
                })),
                templateScriptRefId:
                    this.scriptCompilationStore.getTemplateScriptRefId(this.currentTemplateId) || undefined,
                coderModelId: this.selectedCoderModelId
            };

            const result = await this.dataCrafterTemplatesService.createTemplate(
                this.visualStore.currentProject.id,
                request
            );

            if (!result) {
                message.error('Failed to create template');
                return null;
            }

            if (!options.hideSuccessMessage) {
                message.success('Template created successfully');
            }

            this.setFormChanged(false);

            await this.visualStore.loadTemplates();

            if (!options.stayOnCurrentPage) {
                this.navigateToList();
            } else {
                this.selectedTemplate = result;
            }

            return result;
        } catch (error) {
            console.error('Failed to create template:', error);
            message.error('Failed to create template');
            return null;
        }
    }

    @action.bound
    async updateTemplate(
        templateName: string,
        coderModelId?: string,
        options: SubmitOptions = {}
    ): Promise<DataCrafterTemplate | null> {
        if (!this.canSubmit || !this.visualStore.currentProject || !this.selectedTemplate) {
            return null;
        }

        try {
            this.templateName = templateName;
            this.selectedCoderModelId = coderModelId;
            const preserveIncrementalCandidates = options.preserveIncrementalCandidates === true;

            const request: UpdateDataCrafterTemplateRequest = {
                name: this.templateName,
                outputPackageId: this.selectedOutputPackage!,
                columns: this.outputColumns.filter(col => col.mappingRule),
                inputPackageIds: this.selectedInputPackages,
                inputColumns: this.inputColumns.map(col => ({
                    name: col.name,
                    worksheetName: col.worksheetName,
                    headerCoordinates: col.headerCoordinates,
                    packageId: col.packageId
                })),
                // The save before Compile Script never writes scripts: omitted references keep the saved code,
                // so a stale tab cannot roll back a script another editor saved since.
                templateScriptRefId: preserveIncrementalCandidates
                    ? undefined
                    : this.scriptCompilationStore.currentTemplateScriptRefId || undefined,
                status: this.selectedTemplate.status,
                coderModelId: this.selectedCoderModelId,
                // Update template request with output variants from the store
                outputVariants: this.outputVariantsStore.outputVariants.map(variant => ({
                    id: variant.variantId,
                    name: variant.name,
                    condition: variant.condition,
                    columns: variant.columns,
                    isDefault: variant.isDefault,
                    scriptRefId: preserveIncrementalCandidates ? undefined : variant.scriptRefId
                })),
                variantPickerScriptRefId:
                    !preserveIncrementalCandidates && this.outputVariantsStore.outputVariants.length > 0
                        ? this.scriptCompilationStore.getVariantPickerScriptRefId(this.currentTemplateId) || undefined
                        : undefined
            };

            const result = await this.dataCrafterTemplatesService.updateTemplate(
                this.visualStore.currentProject.id,
                this.selectedTemplate.id,
                request
            );

            if (!result) {
                message.error('Failed to update template');
                return null;
            }

            if (!options.hideSuccessMessage) {
                message.success('Template updated successfully');
            }

            if (!preserveIncrementalCandidates) {
                this.scriptCompilationStore.clearIncrementalCandidates(this.currentTemplateId);
            }

            // Unsaved results on any target (main script or variant) still need Update Template.
            this.setFormChanged(this.scriptCompilationStore.hasAnyIncrementalCandidate(this.currentTemplateId));

            await this.visualStore.loadTemplates();

            if (!options.stayOnCurrentPage) {
                this.navigateToList();
            }

            return result;
        } catch (error) {
            console.error('Failed to update template:', error);
            message.error('Failed to update template');
            return null;
        }
    }

    @action.bound
    setFormChanged(formChanged: boolean) {
        this.formChanged = formChanged;
    }

    @action.bound
    reset() {
        this.selectedTemplate = null;
        this.selectedInputPackages = [];
        this.selectedOutputPackage = null;
        this.inputColumns = [];
        this.outputColumns = [];
        this.mlModels = [];

        this.templateName = '';
        this.selectedCoderModelId = undefined;
        this.selectedInputPackageId = undefined;

        // Reset output variants data
        this.outputVariantsStore.reset();

        // Reset script compilation store
        this.scriptCompilationStore.reset();

        this.setCodeValidationContext(undefined);
        this.setFormChanged(false);
        this.setOutputColumnsActiveTab(undefined);
    }

    @action.bound
    async runTemplateScript(): Promise<void> {
        if (!this.canPreviewScript) {
            return;
        }

        // Run the script which is selected and do not use output variants to avoid confusion
        await this.scriptCompilationStore.runTemplateScriptJob(
            this.visualStore.currentProject!.id,
            this.currentTemplateId,
            this.selectedInputPackages,
            this.selectedOutputPackage!,
            this.displayTemplateScriptRefId,
            [],
            null,
            this.outputVariantsStore.selectedOutputVariant?.variantId
        );
    }

    @action.bound
    async validateTemplateScript(modelId: string): Promise<void> {
        if (!this.canValidateScript) {
            return;
        }

        // Set validation context from the compilation store after validation
        this.setCodeValidationContext(undefined);
        await this.scriptCompilationStore.validateTemplateScript(
            this.selectedInputPackages,
            this.selectedOutputPackage!,
            this.displayTemplateScript!,
            this.displayOutputColumns.filter(col => col.mappingRule),
            modelId
        );

        // Update local validation context from the compilation store
        this.setCodeValidationContext(this.scriptCompilationStore.codeValidationContext);
    }

    @action.bound
    async compileTemplateScriptWithProgress(): Promise<void> {
        const templateId = this.currentTemplateId;
        const projectId = this.visualStore.currentProject?.id;

        if (!this.canCompileScript || !projectId) {
            return;
        }

        await this.scriptCompilationStore.compileTemplateScriptWithProgress(
            this.selectedInputPackages,
            this.selectedOutputPackage!,
            this.displayOutputColumns.filter(col => col.mappingRule),
            this.selectedCoderModelId || null,
            templateId,
            projectId,
            this.outputVariantsStore.selectedOutputVariant || undefined
        );

        this.outputVariantsStore.refreshSelectedOutputVariant();
    }

    @action.bound
    async compileTemplateScriptIncrementallyWithProgress(): Promise<void> {
        const templateId = this.currentTemplateId;
        const projectId = this.visualStore.currentProject?.id;

        if (!this.canCompileScript || !projectId || !this.isEditMode) {
            return;
        }

        const selectedOutputVariant = this.outputVariantsStore.selectedOutputVariant || undefined;
        const compileInputs = JSON.stringify({
            inputPackages: this.selectedInputPackages,
            outputPackage: this.selectedOutputPackage,
            coderModelId: this.selectedCoderModelId,
            variantId: selectedOutputVariant?.variantId,
            mappings: this.displayOutputColumns
                .filter(col => col.mappingRule)
                .map(col => [col.worksheetName, col.name, col.headerCoordinates, col.mappingRule])
        });

        await this.scriptCompilationStore.compileTemplateScriptIncrementallyWithProgress(
            this.selectedCoderModelId || null,
            templateId,
            projectId,
            selectedOutputVariant,
            compileInputs
        );

        this.outputVariantsStore.refreshSelectedOutputVariant();

        // Compiled scripts are not saved until Update Template; warn before leaving the page.
        // A compilation that finishes after the user opened another template must not flag that template.
        if (
            this.currentTemplateId === templateId &&
            this.scriptCompilationStore.hasAnyIncrementalCandidate(templateId)
        ) {
            this.setFormChanged(true);
        }
    }

    @action.bound
    async compileTemplateVariantPickerScriptWithProgress(): Promise<void> {
        const templateId = this.currentTemplateId;
        const projectId = this.visualStore.currentProject?.id;

        if (!this.canCompileScript || !projectId) {
            return;
        }

        await this.scriptCompilationStore.compileTemplateVariantPickerScriptWithProgress(
            this.outputVariantsStore.outputVariants,
            this.selectedCoderModelId || '',
            templateId,
            projectId
        );
    }

    @action.bound
    async testTemplateVariantPickerScript(): Promise<void> {
        const projectId = this.visualStore.currentProject?.id;

        if (!this.canCompileScript || !projectId) {
            return;
        }

        const pickerScriptRefId = this.scriptCompilationStore.getVariantPickerScriptRefId(this.currentTemplateId);
        if (!pickerScriptRefId) {
            return;
        }

        await this.scriptCompilationStore.testTemplateVariantPickerScript(
            projectId,
            this.currentTemplateId,
            this.selectedInputPackages,
            pickerScriptRefId,
            this.outputVariantsStore.outputVariants
        );
    }

    @action.bound
    private async fetchPackagesColumns(packageIds: string[], cancelToken?: CancelToken) {
        try {
            const response = await this.dataCrafterTemplatesService.fetchPackagesColumns(packageIds, cancelToken);
            return response.columns;
        } catch (error) {
            console.error('Failed to fetch packages columns:', error);
            message.error('Failed to fetch packages columns');
            return [];
        }
    }

    // Type guard to ensure template has required properties
    private isValidTemplate(template: unknown): template is DataCrafterTemplate {
        if (!template || typeof template !== 'object') {
            return false;
        }

        const requiredProps = ['id', 'projectId', 'name', 'createdBy', 'outputPackageId', 'columns'];
        const templateObj = template as Record<string, unknown>;

        return requiredProps.every(prop => prop in templateObj) && Array.isArray(templateObj.columns);
    }

    private generateTempId(): string {
        return `${this.tempTemplateIdPrefix}${uuidv4()}`;
    }
}
